import { useGame, TEXT_SCALE_LABELS } from '@/context/GameContext'

// ── Accessibility toggle row ──────────────────────────────────────────────────
function A11yToggle({ label, hint, on, onChange, ts = 1 }: {
  label: string
  hint: string
  on: boolean
  onChange: (v: boolean) => void
  ts?: number
}) {
  const fs = (px: number) => `${(px * ts).toFixed(1)}px`
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        textAlign: 'left', cursor: 'pointer', marginBottom: 12,
        background: on ? 'rgba(52,211,153,0.10)' : 'transparent',
        border: on ? '2px solid #34d399' : '1px solid #312e81',
        borderRadius: 8, padding: '12px 14px',
      }}>
      {/* Switch track */}
      <span aria-hidden style={{
        flexShrink: 0, position: 'relative', width: 44, height: 24, borderRadius: 999,
        background: on ? '#34d399' : '#312e81', transition: 'background 0.15s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18,
          borderRadius: '50%', background: '#0a0a1a', transition: 'left 0.15s',
        }}/>
      </span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontFamily: '"Press Start 2P", monospace',
                       fontSize: fs(8), color: '#e0e7ff', marginBottom: 5 }}>
          {label}
        </span>
        <span style={{ display: 'block', fontFamily: '"Press Start 2P", monospace',
                       fontSize: fs(6), color: '#6b7280', lineHeight: 1.7 }}>
          {hint}
        </span>
      </span>
      <span aria-hidden style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6),
                                 color: on ? '#34d399' : '#6b7280' }}>
        {on ? 'ON' : 'OFF'}
      </span>
    </button>
  )
}

/**
 * Shared accessibility settings panel — used by both the student game and the
 * teacher dashboard. All settings live in GameContext and are persisted.
 * `scaleText` controls whether the panel scales its own font sizes by the
 * text-size setting (true for the student overlay); set false when the host
 * already scales its container (e.g. the teacher dashboard uses CSS zoom).
 */
export default function AccessibilityPanel({ onClose, scaleText = true }: {
  onClose: () => void
  scaleText?: boolean
}) {
  const {
    textScale, textScaleIndex, setTextScaleIndex,
    highContrast, readableFont, reduceMotion, muteMusic,
    setHighContrast, setReadableFont, setReduceMotion, setMuteMusic,
  } = useGame()

  const ts = scaleText ? textScale : 1
  const fs = (px: number) => `${(px * ts).toFixed(1)}px`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(5,5,20,0.85)' }}
         onClick={onClose}
         role="dialog" aria-modal="true" aria-label="Accessibility settings">
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#0f0f23', border: '2px solid #312e81', borderRadius: 12,
                    padding: '22px 24px', maxWidth: 460, width: '100%', maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(12), color: '#a78bfa',
                      textAlign: 'center', marginBottom: 4 }}>
          ♿ ACCESSIBILITY
        </div>
        <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6), color: '#6b7280',
                      textAlign: 'center', marginBottom: 20, lineHeight: 1.8 }}>
          Set things up however works best for you. Your choices are saved.
        </div>

        {/* Text size */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#e0e7ff', marginBottom: 4 }}>
            Text size
          </div>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6), color: '#6b7280', marginBottom: 8, lineHeight: 1.7 }}>
            Make all the words bigger.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {TEXT_SCALE_LABELS.map((label, i) => (
              <button key={label} onClick={() => setTextScaleIndex(i)}
                aria-pressed={textScaleIndex === i}
                style={{
                  flex: 1, fontFamily: '"Press Start 2P", monospace',
                  fontSize: i === 0 ? '8px' : i === 1 ? '11px' : '14px',
                  color: textScaleIndex === i ? '#fbbf24' : '#818cf8',
                  background: textScaleIndex === i ? 'rgba(251,191,36,0.12)' : 'transparent',
                  border: textScaleIndex === i ? '2px solid #fbbf24' : '1px solid #312e81',
                  borderRadius: 6, padding: '10px 6px', cursor: 'pointer',
                }}>
                Aa
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {TEXT_SCALE_LABELS.map(label => (
              <div key={label} style={{ flex: 1, textAlign: 'center',
                fontFamily: '"Press Start 2P", monospace', fontSize: fs(5), color: '#6b7280' }}>
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <A11yToggle ts={ts}
          label="Easy-read font"
          hint="Switches to a rounder font that's easier for many readers."
          on={readableFont} onChange={setReadableFont}/>
        <A11yToggle ts={ts}
          label="High contrast"
          hint="Stronger outlines and brighter text so everything stands out."
          on={highContrast} onChange={setHighContrast}/>
        <A11yToggle ts={ts}
          label="Reduce motion"
          hint="Calms down the moving, spinning, and bouncing animations."
          on={reduceMotion} onChange={setReduceMotion}/>
        <A11yToggle ts={ts}
          label="Mute map music"
          hint="Turns off the overworld soundtrack. Videos still have sound."
          on={muteMusic} onChange={setMuteMusic}/>

        <button onClick={onClose}
          style={{ marginTop: 18, width: '100%', fontFamily: '"Press Start 2P", monospace', fontSize: fs(8),
                   color: '#0a0a1a', background: '#fbbf24', border: 'none',
                   borderRadius: 6, padding: '12px', cursor: 'pointer' }}>
          DONE
        </button>
      </div>
    </div>
  )
}
